import type { ApprovalStage } from './submissions.js';
/** Portal users — who may use the admin portal, and at what privilege. */

export type PortalRole = 'root' | 'admin' | 'staff';
export type PortalUserStatus = 'active' | 'disabled';

export interface PortalUser {
  id: string;
  email: string;
  name: string;
  role: PortalRole;
  status: PortalUserStatus;
  createdAt: string;
  updatedAt: string;
}

export interface CreateUserInput {
  email: string;
  name: string;
  role: Exclude<PortalRole, 'root'>;
}

export interface UpdateUserInput {
  name?: string;
  role?: Exclude<PortalRole, 'root'>;
  status?: PortalUserStatus;
}

/** The signed-in identity returned by /api/admin/me. */
/**
 * Whose decision each approval stage is, for the signed-in person (F10 P2):
 * `mine` — they're a named approver; `others` — someone else is named;
 * `anyone` — nobody is named, so anyone may decide (counts as theirs).
 */
export type StageClaim = 'mine' | 'others' | 'anyone';

export interface MeResponse {
  email: string;
  name: string;
  role: PortalRole;
  /** Per approval stage: whether it's theirs to decide (drives "Waiting for you"). */
  stages?: Record<ApprovalStage, StageClaim>;
}

/** Roles permitted to manage other portal users. */
export function canManageUsers(role: PortalRole): boolean {
  return role === 'root' || role === 'admin';
}
